import { useEffect } from "react";

// @ts-ignore
import logo from "../../../assets/kosmos-logo-white.png";

export default function PrivacyPolicyPage() {
    useEffect(() => {
        document.title = "Kosmos 94.1 • Privacy Policy";
    }, []);

    return (
        <div className="relative isolate bg-gray-800 text-white px-6 pt-14 lg:px-8">
            <div className="mx-auto max-w-5xl py-24 sm:py-40 lg:py-48">
                <div>
                    <img alt="Kosmos 94.1 Logo" className="mx-auto h-18 w-auto" src={logo} />

                    <h1 className="text-4xl mt-10 font-semibold tracking-tight text-balance text-white sm:text-7xl">
                        Kosmos 94.1 Privacy Policy
                    </h1>

                    <p className="mt-10 text-md font-medium text-pretty text-white sm:text-lg/8">
                        Effective date: October 23, 2025 • Last updated: October 23, 2025
                    </p>

                    <p className="mt-10 text-md font-medium text-pretty text-white sm:text-lg/8">
                        At <span className="font-semibold">Kosmos 94.1</span> (“we”, “our”, “us”), we respect your privacy.
                        This Privacy Policy explains how we collect, use, share, and protect your personal information when
                        you use the <span className="font-semibold">Kosmos 94.1 mobile application</span>, website, and related
                        features such as live streaming, podcasts, news, events, and competitions (collectively, the “Services”).
                        By using the Services, you agree to this Policy.
                    </p>

                    {/* 1. Who We Are */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">1. Who We Are</h2>
                    <p className="mt-6 text-md font-medium text-white sm:text-lg/8">
                        Kosmos 94.1 is a Namibian radio station based in Windhoek, Namibia. We provide music, news, podcasts,
                        and community content through our broadcast and digital channels. If you have questions about this Policy,
                        please see “Contact Us” below.
                    </p>

                    {/* 2. Scope */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">2. Scope of This Policy</h2>
                    <p className="mt-6 text-md font-medium text-white sm:text-lg/8">
                        This Policy applies to personal information collected through our app, website, social and communication
                        channels linked from the app, and any in-app forms (e.g., shoutouts, contact, competitions). It does not
                        apply to third-party platforms we link to (e.g., YouTube, Facebook, Instagram).
                    </p>

                    {/* 3. Information We Collect */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">3. Information We Collect</h2>

                    <h3 className="mt-8 text-2xl font-semibold text-white">a) Information You Provide</h3>
                    <ul className="mt-4 list-disc pl-6 text-white sm:text-lg/8">
                        <li>Contact details (name, email address, phone number) submitted via forms.</li>
                        <li>Messages for shoutouts, song requests, feedback, or competition entries.</li>
                        <li>Any other information you choose to share with us in the app.</li>
                    </ul>

                    <h3 className="mt-8 text-2xl font-semibold text-white">b) Information Collected Automatically</h3>
                    <ul className="mt-4 list-disc pl-6 text-white sm:text-lg/8">
                        <li>Device and app data (model, OS, app version), crash logs, and performance metrics.</li>
                        <li>Usage data (pages/screens viewed, features used, session duration).</li>
                        <li>IP address and approximate location for security, analytics, and content localization.</li>
                        <li>Cookies or similar technologies on the website for basic functionality and analytics.</li>
                    </ul>

                    <h3 className="mt-8 text-2xl font-semibold text-white">c) Information from Third Parties</h3>
                    <ul className="mt-4 list-disc pl-6 text-white sm:text-lg/8">
                        <li>Analytics and crash reporting services (e.g., Firebase/Google Analytics, Crashlytics).</li>
                        <li>Embedded or linked content platforms (e.g., YouTube for videos/podcasts, RSS feeds for news).</li>
                        <li>Push notification providers and, if enabled, advertising partners.</li>
                    </ul>

                    {/* 4. How We Use Your Information */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">4. How We Use Your Information</h2>
                    <ul className="mt-6 list-disc pl-6 text-white sm:text-lg/8">
                        <li>To provide the Services (live stream, podcasts, news, competitions, notifications).</li>
                        <li>To communicate with you, including responses to requests and competition administration.</li>
                        <li>To personalize content (e.g., show schedules, local events) where applicable.</li>
                        <li>To monitor performance, fix bugs, and improve features and user experience.</li>
                        <li>To ensure security, prevent misuse, and comply with legal obligations.</li>
                    </ul>

                    {/* 5. Legal Basis */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">5. Legal Basis for Processing</h2>
                    <ul className="mt-6 list-disc pl-6 text-white sm:text-lg/8">
                        <li><span className="font-semibold">Consent:</span> e.g., push notifications, analytics, location (if enabled).</li>
                        <li><span className="font-semibold">Contractual necessity:</span> to deliver requested Services/features.</li>
                        <li><span className="font-semibold">Legitimate interests:</span> app security, quality, analytics, preventing fraud/abuse.</li>
                        <li><span className="font-semibold">Legal obligation:</span> to comply with applicable laws and regulations.</li>
                    </ul>

                    {/* 6. Sharing */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">6. How We Share Your Information</h2>
                    <ul className="mt-6 list-disc pl-6 text-white sm:text-lg/8">
                        <li><span className="font-semibold">Service providers:</span> hosting, streaming, analytics, crash reporting, push notifications, email/SMS.</li>
                        <li><span className="font-semibold">Content platforms:</span> e.g., YouTube or podcast hosts when you interact with embedded content.</li>
                        <li><span className="font-semibold">Legal authorities:</span> if required by law or to protect our rights, users, or the public.</li>
                        <li>We do <span className="font-semibold">not</span> sell or rent your personal data.</li>
                    </ul>

                    {/* 7. Cookies & Tracking */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">7. Cookies and Similar Technologies</h2>
                    <p className="mt-6 text-md font-medium text-white sm:text-lg/8">
                        Our website may use cookies, local storage, or similar technologies to remember preferences and measure usage.
                        You can adjust your browser settings to refuse cookies; some features may not function properly without them.
                    </p>

                    {/* 8. Data Retention */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">8. Data Retention</h2>
                    <p className="mt-6 text-md font-medium text-white sm:text-lg/8">
                        We retain personal data only as long as necessary for the purposes described in this Policy or as required by law.
                        You may request deletion of your personal data, subject to our legal obligations (see “Your Rights” below).
                    </p>

                    {/* 9. Data Security */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">9. Data Security</h2>
                    <ul className="mt-6 list-disc pl-6 text-white sm:text-lg/8">
                        <li>Encryption in transit (TLS) and industry-standard security practices.</li>
                        <li>Access controls, logging, and regular updates to maintain platform security.</li>
                        <li>Reasonable organizational and technical measures to protect your information.</li>
                    </ul>

                    {/* 10. International Transfers */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">10. International Data Transfers</h2>
                    <p className="mt-6 text-md font-medium text-white sm:text-lg/8">
                        Our service providers may process data in countries outside Namibia. Where applicable, we use contractual
                        protections and other safeguards to help ensure an adequate level of protection consistent with this Policy.
                    </p>

                    {/* 11. Your Rights */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">11. Your Data Protection Rights</h2>
                    <ul className="mt-6 list-disc pl-6 text-white sm:text-lg/8">
                        <li>Access, and request a copy of, your personal data.</li>
                        <li>Request correction or deletion of your data where appropriate.</li>
                        <li>Withdraw consent for optional processing (e.g., notifications, analytics, location).</li>
                        <li>Object to or restrict certain processing activities.</li>
                        <li>Lodge a complaint with a relevant supervisory authority.</li>
                    </ul>

                    {/* 12. Children’s Privacy */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">12. Children’s Privacy</h2>
                    <p className="mt-6 text-md font-medium text-white sm:text-lg/8">
                        Our Services are intended for a general audience and are not directed at children under 13. We do not knowingly
                        collect personal data from children. If you believe a child has provided personal information, please contact us
                        so we can delete it.
                    </p>

                    {/* 13. Third-Party Links */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">13. Third-Party Links and Services</h2>
                    <p className="mt-6 text-md font-medium text-white sm:text-lg/8">
                        The Services may link to third-party websites or include embedded content. Those services operate under their own
                        privacy policies. We are not responsible for their practices.
                    </p>

                    {/* 14. Changes */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">14. Updates to This Policy</h2>
                    <p className="mt-6 text-md font-medium text-white sm:text-lg/8">
                        We may update this Privacy Policy from time to time. The “Last updated” date at the top indicates the latest
                        revision. Material changes may be communicated in the app or on our website. Your continued use of the Services
                        means you accept the updated Policy.
                    </p>

                    {/* 15. Contact */}
                    <h2 className="mt-16 text-3xl font-semibold text-white sm:text-4xl">15. Contact Us</h2>
                    <p className="mt-6 text-md font-medium text-white sm:text-lg/8">
                        For privacy inquiries, data access, or deletion requests, please contact:
                    </p>
                    <ul className="mt-4 list-disc pl-6 text-white sm:text-lg/8">
                        <li>
                            Email:{" "}
                            <a
                                href="mailto:info@kosmos.com.na"
                                className="underline decoration-gray-300 hover:decoration-gray-500"
                            >
                                info@kosmos.com.na
                            </a>
                        </li>
                        <li>Kosmos 94.1, Windhoek, Namibia</li>
                        <li>
                            Website:{" "}
                            <a
                                href="https://www.kosmos.com.na"
                                target="_blank"
                                rel="noreferrer"
                                className="underline decoration-gray-300 hover:decoration-gray-500"
                            >
                                www.kosmos.com.na
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
